import {ChangeDetectorRef, Component, EventEmitter, inject, Output, ViewChild} from '@angular/core';
import {EventType, Flow, Location, Partner, ToastConfig, nextTempEventTypeId} from '@app/shared';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {compareById, deepClean, deepClone, deepCompare, deepGet, FlowEventService} from '@app/core';
import {ProjectUtilsService} from '../../../project/services/project-utils.service';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {forkJoin, lastValueFrom, Observable} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NZ_DRAWER_DATA, NzDrawerRef} from 'ng-zorro-antd/drawer';
import {GlobalValuesService} from '../../../../core/services/global-values.service';

@Component({
  selector: 'app-flow',
  standalone: false,

  templateUrl: './flow.component.html',
  styleUrl: './flow.component.less'
})
export class FlowComponent {

  nzData: { readonly: boolean, flowId?: number } = inject(NZ_DRAWER_DATA);

  @Output() createEvent = new EventEmitter<Flow>();
  @Output() updateEvent = new EventEmitter<Flow>();

  compareById = compareById

  isUpdating = false;

  step: 1 | 2 = 1;

  procesingRequest: number = 0;

  serviceForm!: FormGroup;
  usersList: Partner[] = [];
  location: Location[] = [];
  flowsList: Flow[] = [];

  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Servicio creado.',
    showUndo: false
  };

  documentTypes: { name: string, id: number }[] = [];

  notRef: any;
  @ViewChild('notificationTmpl') notificationTmpl: any;

  eventForm: FormGroup | undefined;
  activatorForm: FormGroup | undefined;
  selectedEvent: EventType | undefined;
  isFirstTime = true;
  partnerOptions: Partner[]=[]

  activadorNameList = ['Subida documento', 'Validacion documento', 'Fecha Mojito', 'Manual'];

  dataReady: boolean = false;

  flowName: string = '';

  constructor(private fb: FormBuilder, private ps: ProjectUtilsService,
              private drawerRef: NzDrawerRef<string>,
              private notificationService: NzNotificationService,
              private cdr: ChangeDetectorRef,
              private globalValuesService: GlobalValuesService,
              private flowEventService: FlowEventService) {
    globalValuesService.isFlowDataReady.pipe(takeUntilDestroyed()).subscribe((value) => {
      if (value) {
        this.globalValuesService.flows$.pipe(takeUntilDestroyed())
          .subscribe((value) => this.flowsList = deepClone(value));
        this.initForms();
      }
    })
    this.globalValuesService.places$.pipe(takeUntilDestroyed())
      .subscribe(value => this.location=value)
    this.globalValuesService.isPartnerReady$.pipe(takeUntilDestroyed())
      .subscribe(value => {
        if(value){
          this.partnerOptions=this.globalValuesService.partnerlist
        }
      })
  }

  get events(): FormArray {
    return this.serviceForm.get('eventTypes') as FormArray;
  }

  openAddOrEditEvent(eventId: number | null): void {
    this.selectedEvent = eventId ? this.events.value.find((e: EventType) => e.id === eventId) : undefined;

    this.activatorForm = this.fb.group({
      name: [this.selectedEvent?.activator?.name || this.activadorNameList[0], Validators.required],
      value: [this.selectedEvent?.activator?.value, Validators.required],
      location: [this.selectedEvent?.activator?.location || null],
    });

    this.eventForm = this.fb.group({
      id: [this.selectedEvent?.id || nextTempEventTypeId()],
      name: [this.selectedEvent?.name || '', Validators.required],
      sequence: [this.selectedEvent?.sequence || this.events.length + 1, Validators.required],
      icon: [this.selectedEvent?.icon || '', Validators.required],
      color: [this.selectedEvent?.color || '', Validators.required],
      activator: this.activatorForm,
    })

    this.step = 2;
  }

  confirmAddOrEditEvent(): void {
    if (this.selectedEvent) {
      const index = this.events.value.findIndex(
        (control: any) => control.id === this.selectedEvent?.id
      );

      // Actualizar el FormControl en el FormArray
      this.events.at(index).patchValue({
        ...this.selectedEvent,
        ...this.eventForm?.value
      });
    } else {
      const currentLength = this.events!.length;
      const insertIndex = Math.max(0, currentLength - 1);

      // 1. Configurar sequence del NUEVO evento (penúltimo)
      this.eventForm!.get('sequence')?.setValue(currentLength); // Usamos currentLength, no +1

      // 2. Insertar en penúltima posición
      this.events!.insert(insertIndex, this.eventForm!);

      // 3. Actualizar el ULTIMO elemento (que ahora está desplazado)
      if (currentLength > 0) {
        const lastIndex = this.events!.length - 1;
        const lastControl = this.events!.at(lastIndex);
        lastControl.get('sequence')?.setValue(currentLength + 1);
      }
    }
    this.selectedEvent = undefined;
    this.step = 1;
  }

  selectFlow(flow: Flow): void {
    if (this.isFirstTime) {
      this.isFirstTime = false;
      this.serviceForm.get('flow')?.setValue(this.flowsList[0]);
    }

    this.flowName = this.serviceForm?.value?.flow?.name;

    // Limpia el FormArray y agrega controles basados en el flujo seleccionado
    this.events.clear();
    if (flow?.events) {
      flow?.events.sort((a: EventType, b: EventType) => a.sequence - b.sequence).forEach(evt => {
        this.events.push(this.fb.group({
          id: [evt.id, Validators.required],
          name: [evt.name, Validators.required],
          sequence: [evt.sequence, Validators.required],
          icon: [evt.icon, Validators.required],
          color: [evt.color, Validators.required],
          activator: [evt.activator, Validators.required],
        }));
      });
    }
  }

  close(): void {
    this.clear();
    this.drawerRef?.close()
  }

  clear(): void {
    this.step = 1;
    this.flowsList = [];
    this.serviceForm?.reset();
    this.eventForm?.reset();
    this.selectedEvent = undefined;
    this.isFirstTime = true;
  }

  cancelAddOrEditEvent(): void {
    this.selectedEvent = undefined;
    this.step = 1;
  }

  isFormsValids(): boolean {
    const isEventValid = this.eventForm!.get('color')?.value && this.eventForm!.get('icon')?.value && this.eventForm!.get('name')?.value && this.eventForm!.get('sequence')?.value
    const isManual = this.activatorForm!.get('name')?.value === 'Manual';
    return isEventValid ? (isManual ? true : !!this.activatorForm?.valid) : false;
  }

  initForms(): void {
    this.documentTypes = this.globalValuesService.documentTypes$.value;

    this.usersList = this.partnerOptions;

    this.serviceForm = this.fb.group({
      service: ['', [Validators.required]],
      serviceTypeId: ['', [Validators.required]],
      startDate: [null],
      endDate: [null],
      forwarderId: [null],
      provedorId: [null],
      expeditorId: [null],
      destinationContactId: [null],
      origin: [null],
      destination: [null],
      technicalSpecifications: [null],
      flow: [null, [Validators.required]], // Objeto de flujo principal
      eventTypes: this.fb.array([]), // Lista de eventos relativas al Flujo principal
    });

    // Inicializar el valor del select de Flujos
    this.selectFlow(this.flowsList[0]);

    const initialFlow = this.nzData.flowId
      ? this.flowsList.find(f => f.id === this.nzData.flowId)
      : this.flowsList[0];

    this.serviceForm.get('flow')?.setValue(initialFlow);

    this.events.clear();
    if (this.nzData.flowId) {
      this.flowName = this.serviceForm.value.flow.name;
      if (initialFlow?.events) {
        initialFlow.events.sort((a: EventType, b: EventType) => a.sequence - b.sequence).forEach(evt => {
          this.events.push(this.fb.group({
            id: [evt.id, Validators.required],
            name: [evt.name, Validators.required],
            sequence: [evt.sequence, Validators.required],
            icon: [evt.icon, Validators.required],
            color: [evt.color, Validators.required],
            activator: [evt.activator, Validators.required],
          }));
        });
      }
    } else {
      this.flowName = '';
    }

    this.dataReady = true;
  }

  isValid() {
    return !!this.serviceForm?.value?.flow?.name && !!this.flowName;
  }

  /** Metodo para crear las esntidades de Flow, EventType y EventTrigger **/
  async createEventsEntities(isEdit: boolean): Promise<boolean> {
    this.procesingRequest = isEdit ? 2 : 1;
    try {
      // Primero se crea el Flow relacionado al Service creado
      const flowPayload = {
        isNative: true,
        name: this.flowName,
        isActive: true,
        id: isEdit ? this.serviceForm.value.flow.id : null,
      }

      const flowRequest = isEdit
        ? this.flowEventService.updateFlow(deepClean(flowPayload))
        : this.flowEventService.createFlow(deepClean(flowPayload))

      let flowRsp = await lastValueFrom(flowRequest);

      flowRsp.data.events = [];

      this.serviceForm.value.flow.events = this.events.value;

      // Luego se recorren todos lo EventTypes asociados al FLow para crear cada entidad en el
      for (const et of this.serviceForm.value.flow.events) {
        flowRsp.data = await this.createOrEditEvents(et, isEdit, flowRsp.data);
      }

      this.createEvent.emit(flowRsp.data);
      this.procesingRequest = 0;
      this.close();
      return true;

    } catch (e) {
      console.error(e);
      this.procesingRequest = 0;
      return false;
    }
  }

  /** Metodo para editar las esntidades de Flow, EventType y EventTrigger **/
  async editFlow(): Promise<void> {
    this.procesingRequest = 2;

    const originalData = this.serviceForm.value.flow;
    originalData.events = originalData.events || [];
    const editedData: Flow = {
      id: this.serviceForm.value.flow.id,
      events: this.events.value || [],
      name: this.flowName,
    }

    try {
      if (originalData.name !== editedData.name) {
        const flowParams = {
          id: editedData.id,
          name: editedData.name,
          isActive: true,
          isNative: true,
        }

        await lastValueFrom(this.flowEventService.updateFlow(flowParams));
      }

      const changes = {
        added: this.detectAddedEvents(originalData, editedData),
        deleted: this.detectDeletedEvents(originalData, editedData),
        updated: this.detectUpdatedEvents(originalData, editedData),
      };

      if (changes.added.length > 0) {
        for (const et of changes.added) {
          await this.createOrEditEvents(et, false, editedData);
        }
      }

      if (changes.deleted.length > 0) {
        const idsToDelete = changes.deleted.map(i => (i.id));
        await lastValueFrom(this.flowEventService.deleteEventType(idsToDelete));
      }

      if (changes.updated.length > 0) {
        let requests: Observable<any>[] = [];
        for (const et of changes.updated) {
          const {eventTriggerTypeId, eventTypePayload} = this.setEventTypePayload(et, true, editedData.id);
          const eventTriggerPayload = await this.setEventTriggerPayload(et, eventTriggerTypeId, true, et.id)

          requests = [
            ...requests,
            this.flowEventService.updateEventType(eventTypePayload),
            this.flowEventService.updateEventTrigger(eventTriggerPayload)
          ];
        }

        await lastValueFrom(forkJoin(requests));
      }

      this.procesingRequest = 0;
      this.updateEvent.emit(editedData);
      this.close();
    } catch (e) {
      console.error(e);
      this.procesingRequest = 0;
      this.close();
    }
  }

  async createOrEditEvents(et: EventType, isEdit: boolean, flow: Flow): Promise<Flow> {
    const {eventTriggerTypeId, eventTypePayload} = this.setEventTypePayload(et, isEdit, flow.id);

    const eventTypeRequest = isEdit
      ? this.flowEventService.updateEventType(deepClean(eventTypePayload))
      : this.flowEventService.createEventType(deepClean(eventTypePayload))

    // Se crea en BD el EventType con los datos dados
    const eventTypeRsp = await lastValueFrom(eventTypeRequest);

    if ((isEdit && !!et.eventTriggers?.length) || !isEdit) {

      const eventTriggerPayload = await this.setEventTriggerPayload(et, eventTriggerTypeId, isEdit, eventTypeRsp.data.id);

      const eventTriggerRequest = isEdit
        ? this.flowEventService.updateEventTrigger(deepClean(eventTriggerPayload))
        : this.flowEventService.createEventTrigger(deepClean(eventTriggerPayload))

      const eventTriggerRsp = await lastValueFrom(eventTriggerRequest);

      eventTypeRsp.data.eventTriggers = [eventTriggerRsp.data];

      flow.events = flow.events!.filter(e => e.id > 0);
      flow.events.push({...eventTypeRsp.data, activator: et.activator});
    }

    return flow!;
  }

  setEventTypePayload(et: EventType, isEdit: boolean, flowId: number): {
    eventTriggerTypeId: number,
    eventTypePayload: any
  } {

    // Se configuran el eventClassId y el eventTriggerTypeId del EventType
    let eventClassId = 0;
    let eventTriggerTypeId = 0;
    if (et.activator?.name === 'Manual') {
      eventClassId = 1; // (Manual)
      eventTriggerTypeId = 2 // (Manual)
    } else {
      eventClassId = 2; // (Automatico)
      if (et.activator?.name === 'Fecha Mojito') {
        eventTriggerTypeId = 3 // (Fecha Mojito)
      } else if (et.activator?.name === 'Subida documento') {
        eventTriggerTypeId = 1 // (Subida documento)
      } else {
        eventTriggerTypeId = 4 // (Validacion documento)
      }
    }

    // Luego se crea el payload del POST del EventType
    return {
      eventTriggerTypeId,
      eventTypePayload: {
        name: et.name,
        isActive: true,
        sequence: et.sequence,
        flowId: isEdit ? this.serviceForm.value.flow.id : flowId,
        eventClassId: eventClassId,
        color: et.color,
        icon: et.icon,
        locationId: et.activator?.location?.id,
        mojitoDynamicField: et.activator?.mojitoDynamicField?.enum,
        id: isEdit ? et.id : null
      }
    }
  }

  async setEventTriggerPayload(et: EventType, eventTriggerTypeId: number, isEdit: boolean, eventTypeId: number): Promise<any> {

    const eventTriggerData = isEdit
      ? await lastValueFrom(this.flowEventService.getEventTrigger({
        startRow: 0,
        endRow: 1,
        sortProperty: 'id',
        sortDirection: 'asc',
        filterProperty: 'eventTypeId',
        filterValue: et.id.toString()
      }))
      : null;

    const eventTriggerId = isEdit
      ? (et.eventTriggers ? et.eventTriggers![0]!.id : eventTriggerData!.data.rows[0].id)
      : null;

    return {
      name: et.activator?.name || 'Manual',
      integrationField: eventTriggerTypeId === 3 ? et.activator?.value : '-',
      eventTypeId: et.id > 0 ? et.id : eventTypeId,
      documentUploaded: eventTriggerTypeId === 1,
      documentValidation: eventTriggerTypeId === 4,
      documentTypeId: (eventTriggerTypeId === 1 || eventTriggerTypeId === 4) ? et.activator!.value : null,
      eventTriggerTypeId,
      id: eventTriggerId,
    }
  }

  private detectAddedEvents(initial: Flow, edited: Flow): EventType[] {
    return edited.events!.filter(event =>
      event.id < 0 || !initial.events!.some(e => e.id === event.id)
    );
  }

  private detectDeletedEvents(initial: Flow, edited: Flow): EventType[] {
    return initial.events!.filter(initialEvent =>
      !edited.events!.some(e => e.id === initialEvent.id)
    );
  }

  private detectUpdatedEvents(initial: Flow, edited: Flow): EventType[] {
    return edited.events!.filter(editableEvent => {
      if (editableEvent.id < 0) return false; // Ignorar nuevos

      const originalEvent = initial.events!.find(e => e.id === editableEvent.id);
      return originalEvent && this.hasEventChanged(originalEvent, editableEvent);
    });
  }

  private hasEventChanged(original: EventType, edited: EventType): boolean {
    // Propiedades a comparar con sus rutas
    const propertiesToCompare = [
      'sequence',
      'name',
      'icon',
      'color',
      'activator.name',
      'activator.location',
      'activator.value'
    ];

    for (const path of propertiesToCompare) {
      const originalValue = deepGet(original, path);
      const editedValue = deepGet(edited, path);

      if (!deepCompare(originalValue, editedValue)) {
        return true; // Hay cambios
      }
    }

    return false; // No hay cambios
  }
}
